import { ReactNode, useEffect, useRef, useState } from "react";
import { CheckCircleIcon, CloseIcon, WarningIcon } from "@chakra-ui/icons";
import {
  Box,
  InputGroup,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { isEmpty } from "ramda";
import { FieldError } from "react-hook-form";

import useDebouncedCallback from "hooks/useDebouncedCallback";
import { TOutput } from "utils/geo/api/Autocomplete";
import VillageWhisperApiEndpoint from "utils/geo/api/VillageWhisper";
import { ILocationSuggestion } from "utils/geo";
import { useLocalization } from "hooks/useLocalization";
import { TranslationKeys } from "utils/localization";
import useApiEndpoint from "hooks/useApiEndpoint";
import { DEFAULT_LOCATIONS } from "src/consts";
import SearchInput from "./components/SearchInput";
import SelectRowItem from "./components/SelectRowItem";

type VillageInputProps = {
  onLocationSelect?: (location: ILocationSuggestion) => void;
  onInputChange?: (input: string) => void;
  withButton?: boolean;
  defaultSearchLocation?: string;
  withBorders?: boolean;
  name: string;
  value?: ILocationSuggestion;
  focusNextElement?: {
    element: string;
    isFocus: boolean;
  };
  error?: FieldError;
  customMessage?: ReactNode;
  customError?: TranslationKeys;
  isNewInsertForm: boolean;
  placeholder?: string;
  isDefaultLocations?: boolean;
};

const VillageInput: React.FC<VillageInputProps> = ({
  onLocationSelect,
  value,
  focusNextElement,
  error,
  customError = "offer.input.village.required",
  customMessage,
  onInputChange,
  isNewInsertForm = false,
  placeholder = "Napiš město, adresu nebo PSČ",
  isDefaultLocations = false,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [clearIcon, setClearIcon] = useState(false);
  const [items, setItems] = useState<TOutput>([]);
  const [chosenCity, setChosenCity] = useState<{
    location: string;
    fetchData: boolean;
  }>({ location: value?.label || "", fetchData: true });

  const isMenuOpen = isOpen && !hasError;

  const { callEndpoint: villageWhisperApi, cancelRequest } = useApiEndpoint(
    VillageWhisperApiEndpoint,
  );
  const { translate } = useLocalization();

  const popperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeModal = (): void => {
    setOpen(false);
  };

  const focusNextEl = (): void => {
    if (!focusNextElement?.element || !focusNextElement.isFocus) {
      return;
    }
    document.getElementById(focusNextElement.element)?.focus();
  };

  useEffect(() => {
    const _chosenCity = items.find((x) => x.label === chosenCity.location);

    if (_chosenCity) {
      onLocationSelect && onLocationSelect(_chosenCity);
      closeModal();
      focusNextEl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, chosenCity]);

  const findProducts = async (term: string): Promise<void> => {
    cancelRequest();
    setLoading(true);
    setError(false);

    villageWhisperApi({ term })
      .then((data) => {
        setError(Boolean(isEmpty(data.data || [])));
        setItems(data?.data || []);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  const onSelect = (loc: ILocationSuggestion): void => {
    setChosenCity({ location: loc.label, fetchData: false });
    onLocationSelect && onLocationSelect(loc);
    closeModal();
    focusNextEl();
  };

  const fetchCity = useDebouncedCallback((term: string) => {
    term.length >= 1 && findProducts(term);
  }, 150);

  useEffect(() => {
    if (chosenCity.fetchData) {
      fetchCity(chosenCity.location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenCity]);

  const setClearIconWrapper = (isHover: boolean): void => {
    setClearIcon(isHover);
  };

  const rightIcon = (): JSX.Element => {
    if (clearIcon) {
      return (
        <CloseIcon
          cursor="pointer"
          color="gray.300"
          onClick={() => {
            setClearIconWrapper(false);
            onInputChange && onInputChange("");
            setChosenCity({ location: "", fetchData: false });
          }}
          onMouseLeave={() => setClearIconWrapper(false)}
          boxSize="15px"
        />
      );
    }

    if (chosenCity.location !== "" && isLoading) {
      return <Spinner color="ud-error" boxSize="20px" />;
    }

    if (items.find((item) => item.label === chosenCity.location)?.id) {
      return (
        <CheckCircleIcon
          color="ud-success"
          boxSize="20px"
          onMouseEnter={() => setClearIconWrapper(true)}
        />
      );
    }

    return (
      <WarningIcon
        color="ud-error"
        boxSize="20px"
        onMouseEnter={() => setClearIconWrapper(true)}
      />
    );
  };

  return (
    <Popover
      isOpen={isMenuOpen}
      autoFocus={false}
      placement="bottom-start"
      isLazy
      matchWidth
    >
      <PopoverTrigger>
        <Box data-test="offer.adrress.town">
          <InputGroup display="block">
            <SearchInput
              error={hasError || Boolean(error)}
              value={chosenCity.location}
              ref={inputRef}
              onBlur={() => setOpen(false)}
              onEnter={(location) => onSelect(location)}
              firstItem={items[0]}
              onTab={(e) => {
                if (isMenuOpen && items[0]?.label) {
                  onSelect(items[0]);
                  e.preventDefault();
                }
              }}
              onChange={(val) => {
                setChosenCity({ location: val, fetchData: true });
                setError(false);
                onInputChange && onInputChange(val);

                if (!value) {
                  setItems([]);
                }
              }}
              onFocus={(isFocused, val) => {
                setOpen(isFocused);
                if (!val) {
                  setItems([]);
                }
              }}
              placeholder={placeholder}
              rightIcon={rightIcon()}
              isNewInsertForm={isNewInsertForm}
            />
          </InputGroup>
          {Boolean(error) && (
            <Text variant="form-error">
              {customError ? translate(customError) : error?.message}
            </Text>
          )}
          {customMessage && <Text variant="form-message">{customMessage}</Text>}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="100%" mt="-5px" data-test="offer.address.town.popover">
        <PopoverBody
          p="0"
          ref={popperRef}
          _focus={{ boxShadow: "0" }}
          maxH={isDefaultLocations ? "200px" : "unset"}
          overflowY="auto"
        >
          {!hasError && isEmpty(items) && (
            <>
              {isDefaultLocations ? (
                <>
                  {DEFAULT_LOCATIONS.map((item, i) => (
                    <SelectRowItem key={i} item={item} onSelect={onSelect} />
                  ))}
                </>
              ) : (
                <Box p="10px">
                  <Text variant="small_1">
                    Začněte psát a my vám zkusíme poradit.
                  </Text>
                </Box>
              )}
            </>
          )}

          <Box _last={{ div: { borderBottomRadius: "7px" } }}>
            {!isEmpty(items) &&
              (items as Array<TOutput[0]>).map((item, i) =>
                !item.id ? (
                  <Box key={i} px="15px" py="10px">
                    <Text variant="small_1" color="ud-grey-600">
                      {item.label}
                    </Text>
                  </Box>
                ) : (
                  <SelectRowItem key={i} item={item} onSelect={onSelect} />
                ),
              )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default VillageInput;
